(() => {
  'use strict';
  if (window.LabellaSphFilter) return;

  const DEFAULTS = Object.freeze({
    power: 'filter.v.option.sph',
    kind: 'filter.p.m.custom.lb_numara_durumu',
    numbered: 'Numaralı',
    plano: 'Numarasız',
    stock: 'filter.v.availability',
    owner: 'lb_sph_stock',
    carry: 'lb_sph',
  });

  function powerNumber(value) {
    const text = String(value ?? '').trim().replace(/[−–]/g, '-').replace(',', '.');
    if (!/^[+-]?\d{1,2}(?:\.\d{1,2})?$/.test(text)) return null;
    const number = Number(text);
    return Number.isFinite(number) && number !== 0 ? number : null;
  }

  function sortPowers(a, b) {
    const x = powerNumber(a), y = powerNumber(b);
    if (x === null || y === null) return x === null ? (y === null ? 0 : 1) : -1;
    return (x > 0) - (y > 0) || Math.abs(x) - Math.abs(y);
  }

  function matchesSearch(value, query) {
    const normalize = text => String(text).trim().replace(/[−–]/g, '-').replace(/,/g, '.').replace(/\s/g, '');
    return normalize(value).includes(normalize(query));
  }

  // Availability is a permanent collection constraint, not a customer choice.
  function cleanParams(source, keys = DEFAULTS, alwaysNumbered = false) {
    const params = new URLSearchParams(source);
    const kinds = params.getAll(keys.kind);
    const numbered = alwaysNumbered || (kinds.includes(keys.numbered) && !kinds.includes(keys.plano));
    const powers = params.getAll(keys.power).filter(value => powerNumber(value) !== null);
    if (!numbered || !powers.length) {
      params.delete(keys.power);
    } else params.set(keys.power, powers[0]);
    params.delete(keys.owner);
    params.set(keys.stock, '1');
    return params;
  }

  function carryLink(href, pageUrl, power, keys = DEFAULTS) {
    let url, page;
    try { url = new URL(href, pageUrl); page = new URL(pageUrl); } catch { return null; }
    if (url.origin !== page.origin || !url.pathname.includes('/products/')) return null;
    if (powerNumber(power) !== null) url.searchParams.set(keys.carry, power);
    else url.searchParams.delete(keys.carry);
    return url.pathname + url.search + url.hash;
  }

  class CollectionPowerFilter {
    constructor(config) {
      this.keys = { ...DEFAULTS, ...config.keys };
      this.alwaysNumbered = config.alwaysNumbered === true;
      this.root = document.getElementById('main-collection-filters');
      this.grid = document.getElementById('ProductGridContainer');
      if (!this.root || !this.grid) return;
      this.pendingFrame = 0;
      this.inputHandler = event => this.onInput(event);
      this.linkHandler = event => this.onLink(event);
      document.addEventListener('input', this.inputHandler, true);
      document.addEventListener('click', this.linkHandler, true);
      this.observer = new MutationObserver(records => {
        const ownSelector = '[data-lb-sph-owner],[data-lb-sph-stock-guard],.lb-sph-search-wrap,[data-lb-sph-status]';
        const externalChange = records.some(record => {
          if (record.target.closest?.('[data-lb-sph-list],.lb-sph-search-wrap,[data-lb-sph-status]')) return false;
          const nodes = [...record.addedNodes, ...record.removedNodes];
          return !nodes.length || !nodes.every(node => node.nodeType === 1 && node.matches(ownSelector));
        });
        if (externalChange) this.schedule();
      });
      this.observer.observe(this.root, { childList: true, subtree: true });
      this.observer.observe(this.grid, { childList: true, subtree: true });
      window.addEventListener('popstate', () => this.schedule());
      window.addEventListener('pageshow', () => this.schedule());
      this.sync();
    }

    inputs(name, root = this.root) {
      return [...root.querySelectorAll('input[name]')].filter(input => input.name === name);
    }

    value(input) {
      if (input.value && input.value !== 'on') return input.value;
      return input.closest('label')?.querySelector('.facet-checkbox__text-label')?.textContent.trim() || '';
    }

    forms() {
      return [...this.root.querySelectorAll('form')].filter(form => this.inputs(this.alwaysNumbered ? this.keys.power : this.keys.kind, form).length);
    }

    setChecked(name, values) {
      const chosen = new Set(values);
      this.inputs(name).forEach(input => { input.checked = chosen.has(this.value(input)); });
    }

    setOwner(owned) {
      this.owned = owned;
      for (const form of this.forms()) {
        const old = [...form.querySelectorAll('input[data-lb-sph-owner]')];
        if (!owned) { old.forEach(input => input.remove()); continue; }
        if (old.length) continue;
        const marker = document.createElement('input');
        marker.type = 'hidden'; marker.name = this.keys.owner; marker.value = '1';
        marker.dataset.lbSphOwner = '';
        form.append(marker);
      }
    }

    setStockGuard(active) {
      for (const form of this.forms()) {
        const guard = form.querySelector('[data-lb-sph-stock-guard]');
        const hasCheckbox = this.inputs(this.keys.stock, form).some(input =>
          input.type === 'checkbox' && input.checked && !input.disabled && this.value(input) === '1');
        if (!active || hasCheckbox) { guard?.remove(); continue; }
        if (guard) continue;
        // A zero-count native stock checkbox may be disabled or absent. The
        // explicit filter must still reach Shopify and correctly return zero.
        const hidden = document.createElement('input');
        hidden.type = 'hidden'; hidden.name = this.keys.stock; hidden.value = '1';
        hidden.dataset.lbSphStockGuard = ''; form.append(hidden);
      }
    }

    clearPower() {
      this.setChecked(this.keys.power, []);
      this.setOwner(false);
      this.setChecked(this.keys.stock, ['1']);
      this.setStockGuard(true);
    }

    showPower(numbered) {
      const details = [...new Set(this.inputs(this.keys.power).map(input => input.closest('.js-filter')).filter(Boolean))];
      for (const detail of details) {
        detail.classList.add('lb-sph-facet');
        detail.classList.toggle('lb-sph-visible', numbered);
        detail.classList.toggle('lb-sph-hidden', !numbered);
        if (!numbered) detail.open = false;
        const title = detail.querySelector('.facets__summary span, .mobile-facets__summary span');
        if (title && title.textContent.trim() !== 'Hangi numarayı arıyorsunuz?') title.textContent = 'Hangi numarayı arıyorsunuz?';
        this.mountSearch(detail);
      }
    }

    onInput(event) {
      const input = event.target;
      if (!this.enabled || !(input instanceof HTMLInputElement) || !this.root.contains(input)) return;
      if (input.hasAttribute('data-lb-sph-search')) {
        event.stopImmediatePropagation();
        this.search(input.closest('.js-filter'), input.value);
        return;
      }
      if (input.name === this.keys.power) {
        const power = input.checked ? this.value(input) : '';
        if (power && powerNumber(power) === null) { input.checked = false; return; }
        this.setChecked(this.keys.power, power ? [power] : []);
        if (power) {
          this.setChecked(this.keys.kind, [this.keys.numbered]);
          this.setChecked(this.keys.stock, ['1']);
          this.setOwner(false);
          this.setStockGuard(true);
        } else this.clearPower();
      } else if (input.name === this.keys.kind) {
        // One status at a time; synchronize desktop and mobile forms immediately.
        const selected = input.checked ? [this.value(input)] : [];
        this.setChecked(this.keys.kind, selected);
        const numbered = selected.includes(this.keys.numbered);
        if (!numbered) this.clearPower();
        this.showPower(numbered);
      }
      this.setChecked(this.keys.stock, ['1']);
      this.setStockGuard(true);
      // Dawn's existing form input listener performs its own Ajax refresh.
      // No submit, cart request, scrollTo, history override or theme method patch.
    }

    onLink(event) {
      const anchor = event.target.closest?.('a[href]');
      if (!anchor) return;
      if (this.root.contains(anchor) && anchor.closest('facet-remove')) {
        const url = new URL(anchor.href, location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname) return;
        url.search = cleanParams(url.searchParams, this.keys, this.alwaysNumbered).toString();
        anchor.setAttribute('href', url.pathname + url.search + url.hash);
      } else if (this.enabled && this.grid.contains(anchor)) {
        const params = new URLSearchParams(location.search);
        const kinds = params.getAll(this.keys.kind);
        const power = this.alwaysNumbered || (kinds.includes(this.keys.numbered) && !kinds.includes(this.keys.plano)) ? params.get(this.keys.power) : '';
        const href = carryLink(anchor.getAttribute('href'), location.href, power, this.keys);
        if (href !== null) anchor.setAttribute('href', href);
      }
    }

    schedule() {
      if (this.pendingFrame) return;
      this.pendingFrame = requestAnimationFrame(() => { this.pendingFrame = 0; this.sync(); });
    }

    sync() {
      const params = new URLSearchParams(location.search);
      const kinds = this.inputs(this.keys.kind).filter(input => input.checked).map(input => this.value(input));
      const numbered = this.alwaysNumbered || (kinds.includes(this.keys.numbered) && !kinds.includes(this.keys.plano));
      const powers = this.inputs(this.keys.power);
      const stock = this.inputs(this.keys.stock).filter(input => input.type === 'checkbox');
      stock.forEach(input => input.closest('.js-filter')?.classList.add('lb-sph-stock-facet'));
      this.enabled = !!(powers.length && stock.length);
      this.showPower(this.enabled && numbered);
      if (!stock.length) return;
      this.hasEverEnabled = true;
      this.setOwner(false);
      if (!numbered) this.clearPower();
      this.setChecked(this.keys.stock, ['1']);
      this.setStockGuard(true);
      const customerFilters = [...params.keys()].some(key => key.startsWith('filter.') && key !== this.keys.stock);
      this.root.querySelectorAll('facet-remove').forEach(remove => {
        const anchor = remove.querySelector('a[href]');
        const query = anchor && new URL(anchor.href, location.href).searchParams;
        const stockChip = anchor?.classList.contains('active-facets__button') &&
          params.get(this.keys.stock) === '1' && !query?.has(this.keys.stock);
        const emptyClear = anchor?.classList.contains('active-facets__button-remove') && !customerFilters;
        remove.classList.toggle('lb-sph-hidden', !!stockChip || !!emptyClear);
      });
      const power = numbered ? params.get(this.keys.power) : '';
      this.grid.querySelectorAll('a[href]').forEach(anchor => {
        const href = carryLink(anchor.getAttribute('href'), location.href, power, this.keys);
        if (href !== null && anchor.getAttribute('href') !== href) anchor.setAttribute('href', href);
      });
      // Use Dawn's existing input/Ajax lifecycle for the first stock-only refresh.
      // Do not reload the page, replace history, or make a parallel grid request.
      const needsNormalization = cleanParams(params, this.keys, this.alwaysNumbered).toString() !== params.toString();
      if (!needsNormalization) this.normalizing = null;
      else if (this.normalizing !== location.search) {
        this.normalizing = location.search;
        customElements.whenDefined('facet-filters-form').then(() => {
          const form = this.forms().find(item => item.id === 'FacetFiltersForm') || this.forms()[0];
          const trigger = form && (form.querySelector('[data-lb-sph-stock-guard]') || this.inputs(this.keys.stock, form).find(input => input.checked));
          trigger?.dispatchEvent(new Event('input', { bubbles: true }));
        });
      }
    }

    mountSearch(detail) {
      const list = detail.querySelector('ul');
      if (!list) return;
      const items = [...list.children].filter(item => item.querySelector('input'));
      if (!list.hasAttribute('data-lb-sph-list')) {
        list.dataset.lbSphList = '';
        items.sort((a, b) => sortPowers(this.value(a.querySelector('input')), this.value(b.querySelector('input'))));
        items.forEach(item => {
          if (powerNumber(this.value(item.querySelector('input'))) === null) item.hidden = true;
          list.append(item);
        });
        // Dawn hides values after the first few behind its Show more button.
        // Our bounded scrolling list replaces that behavior for SPH only.
        items.forEach(item => item.classList.remove('hidden', 'show-more-item'));
        const search = document.createElement('div'); search.className = 'lb-sph-search-wrap';
        const field = document.createElement('input');
        field.type = 'search'; field.autocomplete = 'off'; field.placeholder = 'Numara ara: -2,00';
        field.setAttribute('aria-label', 'Göz numaralarında ara'); field.dataset.lbSphSearch = '';
        field.addEventListener('keydown', event => {
          if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); }
        });
        const clear = document.createElement('button'); clear.type = 'button';
        clear.className = 'lb-sph-clear'; clear.textContent = 'Numarayı temizle';
        clear.addEventListener('click', () => {
          const selected = this.inputs(this.keys.power, detail).find(input => input.checked);
          if (!selected) { field.value = ''; this.search(detail, ''); return; }
          selected.checked = false;
          selected.dispatchEvent(new Event('input', { bubbles: true }));
        });
        const status = document.createElement('p'); status.className = 'lb-sph-search-status';
        status.setAttribute('role', 'status'); status.dataset.lbSphStatus = '';
        search.append(field, clear); list.before(search); list.after(status);
      }
      const field = detail.querySelector('[data-lb-sph-search]');
      this.search(detail, field?.value || '');
    }

    search(detail, query) {
      if (!detail) return;
      let count = 0;
      this.inputs(this.keys.power, detail).forEach(input => {
        const show = powerNumber(this.value(input)) !== null && matchesSearch(this.value(input), query);
        const row = input.closest('li');
        if (row) row.hidden = !show;
        if (show) count++;
      });
      const status = detail.querySelector('[data-lb-sph-status]');
      const text = count ? '' : 'Bu aramayla eşleşen numara bulunamadı.';
      if (status && status.textContent !== text) status.textContent = text;
    }
  }

  function applyProductPower(helper, power) {
    if (!helper?.hasAttribute('data-ready') || !helper.config?.powers || !helper.state ||
        !(Number(helper.dataset.powerPosition) > 0) || typeof helper.refresh !== 'function') return false;
    const wanted = powerNumber(power);
    if (wanted === null) return false;
    const option = helper.config.powers.find(item => powerNumber(item.label) === wanted);
    if (!option) return false;
    helper.state.first = option.id;
    helper.state.second = '';
    helper.state.mode = 'same';
    helper.refresh();
    return true;
  }

  function carryToProduct(power) {
    if (powerNumber(power) === null) return;
    const info = document.querySelector('product-info');
    if (!info) return;
    const apply = () => {
      const helper = info.querySelector('lb-lens-picker[data-ready]');
      if (!applyProductPower(helper, power)) return false;
      observer.disconnect(); clearTimeout(timeout); return true;
    };
    const observer = new MutationObserver(apply);
    observer.observe(info, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-ready'] });
    const timeout = setTimeout(() => observer.disconnect(), 20000);
    apply();
  }

  window.LabellaSphFilter = Object.freeze({ powerNumber, sortPowers, matchesSearch, cleanParams, carryLink, applyProductPower });
  const configElement = document.getElementById('lb-sph-filter-config');
  if (!configElement) return;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch { return; }
  if (config.page === 'product') carryToProduct(configElement.dataset.selectedPower || new URLSearchParams(location.search).get(DEFAULTS.carry));
  else if (config.page === 'collection' && ['renkli-lens', 'seffaf-lens'].includes(config.collection)) {
    const start = () => new CollectionPowerFilter(config);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})();
